/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLabels } from '@/hooks/useLabels';
import { useDealDrawerTracking } from '../../../hooks/useDealDrawerTracking';
import { BundleDealOptionV2 } from '../BundleDealOption';
import { ParentSkuCardV2 } from '../ParentSkuCard';
import { NavBar } from '@/components/NavBar';
import type { SkuDetails, DealHash, Deal } from '../../../interface';
import styles from './styles.module.scss';
import { VehicleFitmentBadge } from '../VehicleFitmentBadge';

type Props = {
  parentSkuDetails: SkuDetails;
  skuPrice?: string | number | string[] | number[];
  skuRetailPrice: string | number | string[] | number[];
  skuQuantity: number | string;
  deals: DealHash;
  trackOnDealsAvailableLink: (dealDrawerInfo: {
    dealIdListString: string;
    dealNameListString: string;
    dealDescriptionListString: string;
  }) => void;
  onSelect: (deal: Deal) => void;
  onBackClick: () => void;
  onClose: () => void;
  skuIsVehicleSpecific?: boolean;
};
const labelMap = {
  lblDealBundleListTitle: 'label_deal_bundle_list_title_v2',
};

export const BundleDealOptionList = ({
  parentSkuDetails,
  skuPrice,
  skuRetailPrice,
  skuQuantity,
  deals,
  trackOnDealsAvailableLink,
  onSelect,
  onBackClick,
  onClose,
  skuIsVehicleSpecific,
}: Props) => {
  const labels = useLabels(labelMap);
  useDealDrawerTracking(deals, trackOnDealsAvailableLink);

  return (
    <div className={styles.bundleDealOptionList}>
      <NavBar
        showBackButton={false}
        showCloseButton
        handleBack={onBackClick}
        handleClose={onClose}
      />
      <div className={styles.bundleDealListScroller}>
        <div className={styles.parentSkuSectionV2} data-testid={'parent-sku-details'}>
          <h2 id="deal-drawer-title" className={styles.parentSkuSectionTitleV2}>
            {labels.lblDealBundleListTitle}
          </h2>
          <ParentSkuCardV2
            skuDetails={parentSkuDetails}
            skuQuantity={skuQuantity}
            skuPrice={skuPrice}
            skuRetailPrice={skuRetailPrice}
          />
        </div>
        <div className={styles.bundleDealOptionListMainBody}>
          <VehicleFitmentBadge skuIsVehicleSpecific={skuIsVehicleSpecific} mode="dealsForYour" />
          <ul className={styles.dealsContainerV2}>
            {Object.entries(deals).map(([dealKey, deal]) => {
              return <BundleDealOptionV2 key={dealKey} deal={deal} onSelect={onSelect} />;
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
